import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../../zodios/client";
import { useAppSelector } from "../../../../redux/hooks";
import { AdPlanContext } from "./AdPlanContext";
import { toAdPlanData } from "./toAdPlanData";
import type { AdPlan, ToAdPlanDataContext } from "./types";
import { successToast, errorToast } from "../../../../utils/toasts";

export function useCreateAdPlan({ onSuccess }: { onSuccess?: () => void } = {}) {
  const { t } = useTranslation(["advertising"]);
  const queryClient = useQueryClient();
  const { setRefetchOnUpdate } = useContext(AdPlanContext);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const user = useAppSelector((state) => state.user);

  const ctx: Omit<ToAdPlanDataContext, "adId"> = {
    marketPlace,
    acosOrRoas: user.preferences.acos_or_roas,
    organizationType: user.organization_type ?? "",
  };

  const createOnServer = async (adPlan: AdPlan, adId?: number): Promise<number> => {
    const data = toAdPlanData(adPlan, { adId, ...ctx });

    if (!data) throw new Error("No ad plan data to submit");

    const res = await apiClient.createAdvertisement(data);
    const advertisementId = res.advertisement_id;

    // Recursively create children for composite ads
    if (adPlan.canHaveChildren && adPlan.children && adPlan.children.length > 0) {
      const parentName = adPlan.name;
      for (const child of adPlan.children) {
        const splitName = child.name?.split(" - ") ?? [];
        const childTacticName = splitName[splitName.length - 1];
        const formattedName = `${parentName} - ${childTacticName}`;
        const childAdplan: AdPlan = {
          ...child,
          parent: advertisementId,
          name: formattedName,
          categoryId: adPlan.categoryId,
        };
        await createOnServer(childAdplan);
      }
    }

    return advertisementId;
  };

  return useMutation({
    mutationFn: async ({ adPlan, adId, notify = true }: { adPlan: AdPlan; adId?: number; notify?: boolean }) => {
      await createOnServer(adPlan, adId);
      return { notify };
    },

    onSuccess: ({ notify }) => {
      void queryClient.invalidateQueries();
      setRefetchOnUpdate(true);
      if (notify) {
        successToast({ message: t(($) => $.advertising["Activating Media Plan"]) });
        onSuccess?.();
      }
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Activate Media Plan"]), error });
    },
  });
}
